//Framework Imports
import { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { Prompt, useHistory, useLocation, useParams } from 'react-router-dom';
import { Role, TalentType } from '../../../common/constants';
import { useAppSelector } from '../../../app/hooks';

//Component Imports
import { FieldInput } from '../../../components/fieldInput';
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import { Notification } from '../../../components/notifications';
import { Tab } from '@headlessui/react';
import { ResponseStatus } from '../../../components/notifications';

//Data Imports
import { getStates } from '../../../api/services/stateService';
import { getDivisions } from '../../../api/services/divisionService';
import { getDisciplines } from '../../../api/services/disciplineService';
import { getDistricts } from '../../../api/services/districtService';

//Model Imports
import Payrate from '../../../api/models/payrate';
import Discipline from '../../../api/models/discipline';
import District from '../../../api/models/district';
import ReactPhoneInput from 'react-phone-input-2';
import { getTherapistByUserId, searchTherapists, updateTherapist } from '../../../api/services/therapistService';
import { createPayrate, updatePayrate, createPayrateBilling, updatePayrateBilling, getPayrateById, getPayratesByTherapist, deletePayrate } from '../../../api/services/payrateServices';
import { selectOption } from "../../../types/selectOption";
import Division from '../../../api/models/division';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import EditModal from '../../../components/editModal';
import TherapistRequest from '../../../api/requests/therapistRequest';
import payrate from '../../../api/models/payrate';
import { createBonus, deleteBonus, getBonusById, getBonusesByUserId, getBonusFrequencies, getBonusTypes, updateBonus } from '../../../api/services/bonusServices';
import Bonus from '../../../api/models/bonus';
import { createSupervisor, getSupervisorsByTherapistId } from '../../../api/services/supervisorServices';
import Supervisor from '../../../api/models/supervisor';
import Therapist from '../../../api/models/therapist';
import SupervisorRequest from '../../../api/requests/supervisorRequest';
import BonusRequest from '../../../api/requests/bonusRequest';
import SelectMulti from 'react-select';
import PayrateRequest from '../../../api/requests/payrateRequest';
import BonusTypes from '../../../api/models/bonusTypes';
import BonusFrequency from '../../../api/models/bonusFrequency';
import { hasRole, hasValue, isDate, LCFirst } from '../../../common/utils';
import { ConfirmPendingChanges, paymentScheduleOptions, statusOptions } from '../../../common/constants';
import User from '../../../api/models/user';
import TherapistSearchRequest from '../../../api/requests/therapistSearchRequest';
import { DistrictProgramSelectOption } from '../../../api/models/districtProgramSelectOption';
import PendingTherapistChanges from '../../../api/models/pendingTherapistChanges';
import { IsAllowed, IsAllowedExcept } from '../../../components/displayFor';

//Get our view state
type ViewState = {
  message?: string;
  showPending?: boolean;
};

//GET params
type Params = { id: string; };

//Async fetch functions
const fetchTherapist = async ({ id }: any) => await getTherapistByUserId(id);
const fetchSupervisors = async ({ id }: any) => await getSupervisorsByTherapistId(Number(id));
const fetchBonuses = async ({ id }: any) => await getBonusesByUserId(Number(id));
const fetchDistricts = async () => getDistricts();
const fetchTherapists = async () => await searchTherapists({ therapistId: undefined, disciplineId: undefined, status: undefined, tricoreId: undefined } as TherapistSearchRequest);
const fetchPayrates = async (args: any) => await getPayratesByTherapist(args[1], args[0]);
const deleteRate = async (args: any) => await deletePayrate(args[0]);
const deleteBonusFunc = async (args: any) => await deleteBonus(args[0]);

const EditTherapist = () => {
  //Get our parameter and state data
  const { state } = useLocation<ViewState>();
  const { id } = useParams<Params>();
  const history = useHistory();

  const currentUser = useAppSelector((s) => s.user);
  const userRoles = useAppSelector((s) => s.user.roles);

  //used to track if the user made changes to the page before exiting
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const changesMade = () => { setHasChanges(true); }

  //Data
  const [divisionId, setDivisionId] = useState<number>(0);
  const [stateId, setStateId] = useState<number>(0);
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  const [disciplinesOptions, setDisciplinesOptions] = useState<selectOption[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<selectOption[]>([]);
  const [stateOptions, setStateOptions] = useState<selectOption[]>([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState<Discipline[]>();
  const [selectedDivisions, setSelectedDivisions] = useState<selectOption[]>();
  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [referringTherapistOptions, setReferringTherapistOptions] = useState<selectOption[]>([]);
  const [supervisorOptions, setSupervisorOptions] = useState<selectOption[]>([]);
  const [discipline] = useState<Discipline>();
  const [therapistDisciplines, setTherapistDisciplines] = useState<Discipline[]>([]);
  const [therapistDisciplinesOptions, setTherapistDisciplinesOptions] = useState<selectOption[]>([]);
  const [therapistDisciplinesActiveOptions, setTherapistDisciplinesActiveOptions] = useState<selectOption[]>([]);
  const [bonusTypes, setBonusTypes] = useState<BonusTypes[]>([]);
  const [bonusFrequencies, setBonusFrequencies] = useState<BonusFrequency[]>([]);
  const [pendingChanges, setPendingChanges] = useState<PendingTherapistChanges>();
  const [activeDistrictIds, setActiveDistrictIds] = useState<number[]>();
  const [user, setUser] = useState<User>();

  //Therapist Fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleInitial, setMiddleInitial] = useState('');

  //Email Fields
  const [email, setEmail] = useState<string>('');
  const [secondaryEmail, setSecondaryEmail] = useState<string>('');
  const [tricoreId, setTricoreId] = useState<number | undefined>();
  const [quickbooksListId, setQuickbookListId] = useState('');
  const [primaryCmsUserId, setPrimaryCmsUserId] = useState('');

  //Address Fields
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('');
  const [paymentSchedule, setPaymentSchedule] = useState<string>('');

  //CFY/Intern Fields
  const [cfyStartDate, setCfyStartDate] = useState<Date | null>(null);
  const [cfyEndDate, setCfyEndDate] = useState<Date | null>(null);
  const [internStartDate, setInternStartDate] = useState<Date | null>(null);
  const [internEndDate, setInternEndDate] = useState<Date | null>(null);

  //Referring Therapist
  const [referringTherapistId, setReferringTherapistId] = useState<number>(0);
  const [superviseeId, setSuperviseeId] = useState<number>(0);
  const [comments, setComments] = useState('');

  //Pay Rates Fields
  const [payratesPast, setPayratesPast] = useState<Payrate[]>([]);
  const [payratesActive, setPayratesActive] = useState<Payrate[]>([]);
  const [payratesPending, setPayratesPending] = useState<PayrateRequest[]>([]);
  const [payratesScheduled, setPayratesScheduled] = useState<PayrateRequest[]>([]);

  //Supervisor Modal
  const [supervisorModalOpen, setSupervisorModalOpen] = useState(false);
  const [supervisorModalFirstName, setSupervisorModalFirstName] = useState('');
  const [supervisorModalLastName, setSupervisorModalLastName] = useState('');
  const [supervisorModalType, setSupervisorModalType] = useState('');

  //Bonus Modal
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [bonusModalType, setBonusModalType] = useState('');
  const [bonusModalFrequency, setBonusModalFrequency] = useState('');
  const [bonusModalDivision, setBonusModalDivision] = useState<number>();
  const [bonusModalDiscipline, setBonusModalDiscipline] = useState<number>();
  const [bonusModalStart, setBonusModalStart] = useState<Date | null>(null);
  const [bonusModalEnd, setBonusModalEnd] = useState<Date | null>(null);
  const [bonusModalAmount, setBonusModalAmount] = useState<number | null>(null);
  const [bonusModalPaymentAmounts, setBonusModalPaymentAmounts] = useState<number | null>(null);
  const [bonusModalComment, setBonusModalComment] = useState('');

  //Payrates Modal
  const [payratesModalOpen, setPayratesModalOpen] = useState(false);
  const [isPastRate, setIsPastRate] = useState<boolean>(false);
  const [payratesModalRate, setPayratesModalRate] = useState('');
  const [payratesModalDistrict, setDistrict] = useState<number>(-1);
  const [payratesModelProgramId, setProgram] = useState<number | undefined>();
  const [payratesModalStartDate, setPayratesModalStartDate] = useState<Date | null>(null);
  const [payratesModalEndDate, setPayratesModalEndDate] = useState<Date | null>(null);
  const [isPayratesModalEditing, setIsPayratesModalEditing] = useState<boolean>(false);
  const [isBonusModalEditing, setIsBonusModalEditing] = useState<boolean>(false);
  const [payratesModalPayrateId, setPayratesModalPayrateId] = useState<number>(0);
  const [selectedPayrate, setSelectedPayrate] = useState<Payrate>();
  const [selectedPayrateId, setSelectedPayrateId] = useState<number>();
  const [selectedBonus, setSelectedBonus] = useState<Bonus>();
  const [selectedBonusId, setSelectedBonusId] = useState<number>();

  const [districtOptions, setDistrictOptions] = useState<DistrictProgramSelectOption[]>([{ label: '', districtId: -1 }]);
  const [districs, setDistricts] = useState<District[]>([]);

  const [userId, setUserId] = useState<number>();
  const [payrateDisciplineId, setPayrateDisciplineId] = useState<number>(-1);
  const [payrateDiscipline, setPayrateDiscipline] = useState<Discipline>();
  const [isPending, setIsPending] = useState<boolean>(false);
  const [bullhornTalentId, setBullhornTalentId] = useState<number | string | undefined>();
  const [talentType, setTalentType] = useState<string | undefined>('');

  const [isOperations] = useState<boolean>(currentUser && hasRole(currentUser.roles!, Role.Operations));
  const [isAdmin] = useState<boolean>(currentUser && hasRole(userRoles!, Role.Admin));
  const [isBillingSpecialist] = useState<boolean>(currentUser && hasRole(userRoles!, Role.BillingSpecialist));
  const [isSuperUser] = useState<boolean>(currentUser && hasRole(currentUser.roles!, Role.SuperUser));
  const [isDirector] = useState<boolean>(currentUser && hasRole(currentUser.roles!, Role.Director));
  //Control vars
  const [readOnly, setReadOnly] = useState<boolean>(false);

  //Generate end date
  const defaultEndDate = new Date();
  defaultEndDate.setFullYear(new Date().getFullYear() + 1);
  defaultEndDate.setMonth(5);
  defaultEndDate.setDate(30);

  /**
   * Updates the payrates any time the payrate discipline changes
   */
  useEffect(() => {
    const idNum = Number(id);
    if (!idNum || idNum < 1 || !payrateDisciplineId || payrateDisciplineId < 1) return;
    payratesAsync.run(idNum, payrateDisciplineId);

    // change the bullhorn ID
  }, [payrateDisciplineId]);

  const payratesAsync = useAsync({ deferFn: fetchPayrates, onResolve: (r) => processPayrates(r.data) });

  /**
   * When disciplinesOptions and therapistDisciplines are both loaded,
   * then we generate the default disciplines
   */
  useEffect(() => {
    const dOptions: selectOption[] = [];
    if (disciplinesOptions !== undefined && therapistDisciplines !== undefined) {
      if (disciplinesOptions.length > 0 && therapistDisciplines.length > 0) {
        //Generate our select options
        therapistDisciplines.map((discipline) => dOptions.push({ value: discipline.disciplineId, label: discipline.name }));
        setPayrateDisciplineId(therapistDisciplines[0].disciplineId ?? 6);
      }
    }

    setTherapistDisciplinesOptions(dOptions);
  }, [disciplinesOptions, therapistDisciplines]);

  /**
   * Data Loading
   */
  const disciplinesData = useAsync({
    promiseFn: getDisciplines,
    onResolve: (r) => {
      const d: Discipline[] = r.data.disciplines;
      const dOptions: selectOption[] = [];
      setDisciplines(d);

      //Loop through the disciplines to generate the options for the multi select
      d.map((discipline) => dOptions.push({ value: discipline.disciplineId ?? 0, label: discipline.name }));
      setDisciplinesOptions(dOptions);
    }
  });

  const districtsData = useAsync({
    promiseFn: fetchDistricts,
    onResolve: (u) => {
      let options: DistrictProgramSelectOption[] = districtOptions;

      setDistricts(u.data.districts);

      //set options           
      u.data.districts.forEach((district, i) => {
        options.push({ districtId: district.districtId ?? -1, label: district.name });

        //add program options
        district.programs.filter(d => d.isVisible).forEach((program, ip) => {
          options.push({
            districtId: district.districtId ?? -1,
            programId: program.programId,
            label: district.name + " - " + program.name
          });
        });

      });

      setDistrictOptions(options);
    },
  });

  /** Get the data for all therapists **/
  const therapistsData = useAsync({
    promiseFn: fetchTherapists,
    onResolve: (r) => {
      const therapists: User[] = r.data.records;
      const rOptions: selectOption[] = [];

      therapists.map((therapist) => rOptions.push({ value: therapist.userId ?? 0, label: `${therapist.lastName} ${therapist.firstName}` }));
      setReferringTherapistOptions(rOptions);
    }
  });

  /** Get the data for all supervisors **/
  const supervisorsData = useAsync({
    promiseFn: fetchSupervisors,
    id,
    onResolve: (r) => {
      const supervisors: Supervisor[] = r.data;
      const sOptions: selectOption[] = [];
      sOptions.push({ value: 0, label: '' });
      (supervisors.map((supervisor) => sOptions.push({ value: supervisor.supervisorId ?? 0, label: `${supervisor.lastName}, ${supervisor.firstName}` })));
      setSupervisorOptions(sOptions);
      setSupervisors(supervisors);
    }
  });

  /** Get the data for the bonuses **/
  const bonusData = useAsync({
    promiseFn: fetchBonuses, id,
    onResolve: (r) => setBonuses(r.data)
  });

  /**
   * Get the data for this specific therapist
   * Primary data fetching function
   */
  const therapistData = useAsync({
    promiseFn: fetchTherapist, id,
    onResolve: (response) => {
      let user: any = response.data;
      let therapists: Therapist[] = user.therapists!;
      let disciplines: Discipline[] = [];

      //set active discipline optioins
      setTherapistDisciplinesActiveOptions(
        therapists.map((t, i) => ({ label: t.discipline?.name ?? "unknown", value: t.discipline?.disciplineId } as selectOption))
      )

      //If we have pending changes, replace the original user object data with the pending data
      if (hasValue(user.pendingChanges)) {
        setIsPending(true);

        const pendingUser = JSON.parse(response.data.pendingChanges!);
        setPendingChanges(pendingUser);

        if (pendingUser && pendingUser?.payrates.length > 0) {
          setPayratesPending(pendingUser.payrates);
        }

        Object.keys(pendingUser).forEach((key, index) => {
          const userKey: string = LCFirst(key);

          if (key !== 'userId' && key !== 'disciplines' && hasValue(pendingUser[key]) && pendingUser[key] !== '')
            user[userKey] = pendingUser[key];
        })
      }

      setEmail(user.emailAddress ?? '');
      setSecondaryEmail(user.secondaryEmailAddress ?? '');
      setFirstName(user.firstName ?? '');
      setMiddleInitial(user.middleInitial ?? '');;
      setLastName(user.lastName ?? '');
      setAddress1(user.address1 ?? '');
      setAddress2(user.address2 ?? '');
      setCity(user.city ?? '');
      setStateId(Number(user.stateId));
      setZipcode(user.zipcode ?? '');
      setPhone(user.phone ?? '');
      setStatus(user.status ?? '');
      setPaymentSchedule(user.paymentSchedule ?? '');
      setCfyStartDate(isDate(user.cfyStartDate) ? moment(user.cfyStartDate).toDate() : null);
      setCfyEndDate(isDate(user.cfyEndDate) ? moment(user.cfyEndDate).toDate() : null);
      setInternStartDate(isDate(user.internStartDate) ? moment(user.internStartDate).toDate() : null);
      setInternEndDate(isDate(user.internEndDate) ? moment(user.internEndDate).toDate() : null);
      setReferringTherapistId(user.referringTherapistId ?? 0);
      setSuperviseeId(user.superviseeId === null ? 0 : user.superviseeId!);
      setComments(user.comments ?? '');
      setUserId(user.userId);
      setSelectedDivisions(user.divisions?.map((div: Division) => { return { label: div.name, value: div.divisionId } }));
      setTricoreId(user.tricoreId);
      setQuickbookListId(user.quickbooksListId);
      setPrimaryCmsUserId(user.primaryCmsUserId);
      setBullhornTalentId(user?.therapists[0]?.bullhornTalentId ?? '');
      setTalentType(user?.therapists[0]?.talentType ?? '');
      setUser(user as User);

      if (disciplines.length === 0) {
        therapists?.map((therapist: any) => {
          if (therapist.discipline !== undefined && therapist.discipline !== null) {
            let dis: Discipline = therapist.discipline;
            dis = { ...dis, therapists: [therapist] }
            disciplines.push(dis);
          }
          return true;
        });
      }


      if (disciplines.length > 0) {
        setTherapistDisciplines(disciplines);
        setSelectedDisciplines(disciplines);
        setPayrateDiscipline(disciplines[0]);
      }
      setReadOnly(hasRole(userRoles!, Role.Accounting));
    },
  });

  /** Get the data for all US states **/
  const statesData = useAsync({
    promiseFn: getStates,
    onResolve: (r) => {
      const s: any[] = r.data.states;
      const sOptions: selectOption[] = [];

      //Loop through the disciplines to generate the options for the multi select
      (s.map((state) => sOptions.push({ value: state.stateId ?? 0, label: state.name })));
      setStateOptions(sOptions);
    }
  });

  /** Get the data for all divisions **/
  const divisionsData = useAsync({
    promiseFn: getDivisions,
    onResolve: (r) => {
      const d: Division[] = r.data.divisions;
      const dOptions: selectOption[] = [];

      //Loop through the disciplines to generate the options for the multi select
      (d.map((division) => dOptions.push({ value: division.divisionId ?? 0, label: division.name })));
      dOptions.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
      setDivisionOptions(dOptions);
    }
  });

  /** Get the data for all bonus types **/
  const bonusTypesData = useAsync({
    promiseFn: getBonusTypes,
    onResolve: (r) => setBonusTypes(r.data)
  });

  /** Get the data for all bonus frequencies **/
  const bonusFrequencyData = useAsync({
    promiseFn: getBonusFrequencies,
    onResolve: (r) => setBonusFrequencies(r.data)
  });

  /**
   * Therapist saving async methods
   */
  const saveTherapist = async () => {
    if (!id) {
      return Promise.reject('Missing id.');
    }

    //Process payment schedule
    //const paymentScheduleLabel = paymentScheduleOptions.filter(p => p.value === Number(paymentSchedule))[0]?.label ?? paymentScheduleOptions[0].label;
    const paymentScheduleLabel = paymentSchedule; //the logic above is not working correctly, 

    //Loop through the disciplines to get the ids
    let disciplineIds: number[] = [];
    selectedDisciplines?.forEach(discipline => disciplineIds.push(discipline.disciplineId!));


    const request: TherapistRequest = {
      firstName: firstName,
      middleInitial: middleInitial,
      lastName: lastName,
      emailAddress: email,
      secondaryEmailAddress: secondaryEmail,
      userId: userId,
      tricoreId: tricoreId,
      quickbooksListId: quickbooksListId,
      primaryCmsUserId: primaryCmsUserId,
      address1: address1,
      address2: address2,
      city: city,
      stateId: stateId,
      zipcode: zipcode,
      phone: phone,
      status: status,
      paymentSchedule: paymentScheduleLabel,
      disciplineId: discipline?.disciplineId,
      disciplines: selectedDisciplines,
      disciplineIds: disciplineIds,
      supervisors: [],
      cfyStartDate: cfyStartDate?.toLocaleDateString(),
      cfyEndDate: cfyEndDate?.toLocaleDateString(),
      internStartDate: internStartDate?.toLocaleDateString(),
      internEndDate: internEndDate?.toLocaleDateString(),
      bonus: [],
      referringTherapistId: referringTherapistId,
      superviseeId: superviseeId,
      comments: comments,
      divisionIds: selectedDivisions?.map((option) => { return option.value! }) ?? []
    }

    return await updateTherapist(id, request);
  };

  /**
   * Save Therapist
   */
  const save = useAsync({
    deferFn: saveTherapist,
    onResolve: (r) => {
      setHasChanges(false);
      history.push(`/admin/therapists/${id}`);
      window.scrollTo(0, 0);

      //update discipline options
      const activeDisciplines = r.data?.therapists?.map((t, i) => ({ label: t.discipline?.name ?? "unknown", value: t.discipline?.disciplineId } as selectOption));
      if (activeDisciplines !== undefined)
        setTherapistDisciplinesActiveOptions([...activeDisciplines]);

      if (hasValue(r.data.pendingChanges)) {
        setIsPending(true);

        const pendingUser = JSON.parse(r.data.pendingChanges!);
        setPendingChanges(pendingUser);
      }
    }
  });

  /**
   * Supervisor Methods
   */
  const saveSupervisor = async () => {
    const request: SupervisorRequest = {
      firstName: supervisorModalFirstName,
      lastName: supervisorModalLastName,
      type: supervisorModalType,
      therapistId: Number(id)
    }
    return await createSupervisor(request);
  };
  const saveSupervisorAsync = useAsync({
    deferFn: saveSupervisor,
    onResolve: (r: any) => {
      const supervisor: Supervisor = r.data.supervisor;
      const tmpSupervisors: Supervisor[] = supervisors;
      const sOptions: selectOption[] = [];
      sOptions.push({ value: 0, label: '' });

      tmpSupervisors.push(supervisor);
      (tmpSupervisors.map((supervisor) => (
        sOptions.push({ value: supervisor.supervisorId ?? 0, label: `${supervisor.lastName}, ${supervisor.firstName}` })
      )));

      if (superviseeId == null)
        setSuperviseeId(supervisor.supervisorId);

      setSupervisors(tmpSupervisors);
      setSupervisorOptions(sOptions);
      resetSupervisorsModal();
    }
  });
  const resetSupervisorsModal = () => {
    setSupervisorModalFirstName('');
    setSupervisorModalLastName('');
    setSupervisorModalType('');
    setSupervisorModalOpen(false);
  }

  /**
   * Bonus methods
   */
  const saveBonus = async () => {
    const request: BonusRequest = {
      divisionId: bonusModalDivision,
      disciplineId: bonusModalDiscipline,
      type: bonusModalType,
      frequency: bonusModalFrequency,
      amount: Number(bonusModalAmount),
      paymentAmounts: Number(bonusModalPaymentAmounts),
      startDate: bonusModalStart,
      endDate: bonusModalEnd,
      comments: bonusModalComment,
      userId: Number(id)
    }
    return await createBonus(request);
  };

  const saveBonusAsync = useAsync({
    deferFn: saveBonus,
    onResolve: (r: any) => {
      const bonus: Bonus = r.data.bonus;
      const tmpBonus: Bonus[] = bonuses;

      bonus.disciplineName = disciplinesOptions?.find(x => x.value === bonus?.disciplineId)?.label ?? '';
      bonus.divisionName = divisionOptions?.find(x => x.value === bonus?.divisionId)?.label ?? '';

      tmpBonus.push(bonus);

      setBonuses(tmpBonus);
      resetBonusModal();
    }
  });

  const editBonus = async () => {
    if (selectedBonusId === null)
      return;

    const request: BonusRequest = {
      bonusId: selectedBonusId,
      divisionId: bonusModalDivision,
      disciplineId: bonusModalDiscipline,
      type: bonusModalType,
      frequency: bonusModalFrequency,
      amount: Number(bonusModalAmount),
      paymentAmounts: Number(bonusModalPaymentAmounts),
      startDate: bonusModalStart,
      endDate: bonusModalEnd,
      comments: bonusModalComment,
      userId: Number(id)
    }

    return await updateBonus(selectedBonusId!, request);
  }

  const editBonusAsync = useAsync({
    deferFn: editBonus,
    onResolve: (r: any) => {
      const response = r.data.bonus;
      let tmpBonus: Bonus[] = bonuses;
      let bonus = tmpBonus.find(x => x.bonusId === selectedBonusId);
      if (bonus === undefined) {
        return;
      }

      bonus.type = response.type;
      bonus.frequency = response.frequency;
      bonus.startDate = response.startDate;
      bonus.endDate = response.endDate;
      bonus.amount = response?.amount;
      bonus.paymentAmounts = response.paymentAmounts;
      bonus.comments = response.comments;
      bonus.disciplineName = disciplinesOptions?.find(x => x.value === response.disciplineId)?.label ?? '';
      bonus.divisionName = divisionOptions?.find(x => x.value === response.divisionId)?.label ?? '';

      setBonuses(tmpBonus);
      resetBonusModal();
    }
  })

  const resetBonusModal = () => {
    setBonusModalFrequency('');
    setBonusModalDiscipline(undefined);
    setBonusModalDivision(undefined);
    setBonusModalType('');
    setBonusModalAmount(null);
    setBonusModalPaymentAmounts(null);
    setBonusModalStart(new Date());
    setBonusModalEnd(new Date());
    setBonusModalComment('');
    setIsBonusModalEditing(false);
    setBonusModalOpen(false);
    setHasChanges(false);
  }

  /**
   * Bonus methods
   */
  const savePayrates = async () => {
    const request: PayrateRequest = {
      rate: Number(payratesModalRate),
      startDate: payratesModalStartDate?.toLocaleDateString() ?? undefined,
      endDate: payratesModalEndDate?.toLocaleDateString() ?? undefined,
      therapistId: Number(id),
      disciplineId: Number(payrateDisciplineId),
      districtId: Number(payratesModalDistrict),
      programId: Number(payratesModelProgramId)
    }

    if (isPayratesModalEditing && IsAllowed([Role.BillingSpecialist], false, userRoles)) {
      const pendingRate = await updatePayrateBilling(payratesModalPayrateId, request);
      const payrates: Payrate[] = pendingRate.data;
      processPayrates(payrates);
      return;
    }
    else if (isPayratesModalEditing) {
      const pendingRate = await updatePayrate(payratesModalPayrateId, request);
      setPayratesPending([...payratesPending, pendingRate.data]);
      return;
    }

    if (IsAllowed([Role.BillingSpecialist], false, userRoles)) {
      const response = await createPayrateBilling(request);
      const payrates: Payrate[] = response.data;
      processPayrates(payrates);
    }
    else {
      const pendingRate = await createPayrate(request);
      setPayratesPending([...payratesPending, pendingRate.data]);
    }
  };

  const savePayratesAsync = useAsync({
    deferFn: savePayrates,
    onResolve: (r: any) => {

      //processPayrates(r.data);
      //payratesAsync.run(id);
      resetPayratesModal();
    }
  });

  const resetPayratesModal = () => {
    setPayratesModalRate('');
    setPayratesModalStartDate(null);
    setPayratesModalEndDate(null);
    setPayratesModalOpen(false);
    setIsPayratesModalEditing(false);
    setDistrict(-1);
    setProgram(undefined);
    setHasChanges(false);
  }

  /**
   * Component handling methods
   */
  const handleDisciplineMulti = (options: any) => {
    const newDisciplines: any[] = [];
    let dOptions: selectOption[] = [];
    options.map((opt: any) => newDisciplines.push(disciplines.filter((disc) => opt.value === disc.disciplineId)[0]));
    newDisciplines.map(d => dOptions.push({ value: d.disciplineId ?? 0, label: d.name }));
    dOptions.sort((a, b) => a.label.localeCompare(b.label));
    setTherapistDisciplinesOptions(dOptions);
    setSelectedDisciplines(newDisciplines);//here
  }

  const handleEditPayrate = (payrate: Payrate) => {
    const get = async () => {
      //if (userRoles?.includes(Role.Director)) return;

      const response = await getPayrateById(payrate.payrateId!);
      setSelectedPayrate(response.data);
      setSelectedPayrateId(response.data.payrateId);
      resetPayratesModal();
      setPayratesModalPayrateId(payrate.payrateId!);
      setPayratesModalRate(payrate.rate?.toString()!);
      setDistrict(payrate.districtId!)
      setProgram(payrate.programId);
      setPayratesModalStartDate(new Date(payrate.startDate!));
      setPayratesModalEndDate(new Date(payrate.endDate!));
      setIsPayratesModalEditing(true);

      const startDate: Date = new Date(payrate.startDate ?? new Date());
      const endDate: Date = new Date(payrate.endDate ?? new Date());
      const todaysDate: Date = new Date();
      setIsPastRate(startDate < todaysDate && endDate < todaysDate)
      setPayratesModalOpen(true);
    }

    get();
  }

  const handleEditBonus = (bonus: Bonus) => {
    const get = async () => {
      const response = await getBonusById(bonus.bonusId!);
      setSelectedBonus(response.data)
      setSelectedBonusId(response.data.bonusId);
      resetBonusModal();

      setBonusModalDivision(bonus.divisionId)
      setBonusModalDiscipline(bonus.disciplineId)
      setBonusModalType(bonus.type!);
      setBonusModalFrequency(bonus.frequency!);
      setBonusModalStart(new Date(bonus.startDate!));
      setBonusModalEnd(new Date(bonus.endDate!));
      setBonusModalAmount(bonus.amount!)
      setBonusModalPaymentAmounts(bonus.paymentAmounts!)
      setBonusModalComment(bonus.comments!);
      setSelectedBonusId(bonus.bonusId!);
      setIsBonusModalEditing(true);
      setBonusModalOpen(true);
    }

    get();
  }

  /**
   * Accepts an array of payrates and sets the associated hooks
   * @param payrates 
   */
  const processPayrates = (payrates: Payrate[]): void => {
    if (payrates.length > 0) {
      const past: payrate[] = [], active: payrate[] = [], pending: payrate[] = [], scheduled: payrate[] = [];
      for (let i = 0; i < payrates.length; i++) {
        const pr: Payrate = payrates[i];
        const startDate: Date = new Date(pr.startDate ?? new Date());
        const endDate: Date = new Date(pr.endDate ?? new Date());
        const todaysDate: Date = new Date();

        if (startDate < todaysDate && endDate < todaysDate) {
          past.push(pr);
        } else if (startDate < todaysDate) {
          active.push(pr);
        } else if (startDate > todaysDate) {
          scheduled.push(pr);
        }
      }

      //Set the billing rate states
      setPayratesPast(past);
      setPayratesActive(active);
      setPayratesScheduled(scheduled);
      //setPayratesPending(current => [...current]);

      //update the active payrate labels
      let districtIds: number[] = [];
      active.forEach((payrate) => {
        if (payrate.districtId !== undefined && !districtIds.some((r) => r == payrate.districtId))
          districtIds.push(payrate.districtId);
      });
      setActiveDistrictIds(districtIds);

      return;
    }

    //If no payrates were received, we empty out the arrays
    setPayratesPast([]);
    setPayratesActive([]);
    setPayratesScheduled([]);
    setActiveDistrictIds([]);
  }

  //when the user selects a district/program option
  const districtOptionSelected = (index: number) => {
    var option = districtOptions[index];

    setDistrict(option.districtId);
    setProgram(option.programId);
  }

  // returns the index of the districtOptions based on the user's selection
  const getSelectedDistrictOption = () => {
    if (payratesModalDistrict == -1)
      return 0;

    if (payratesModelProgramId != undefined) {
      for (var i = 0; i < districtOptions.length; i++) {
        if (districtOptions[i].programId === payratesModelProgramId)
          return i;
      }
    }

    for (var i = 0; i < districtOptions.length; i++) {
      if (districtOptions[i].districtId === payratesModalDistrict && districtOptions[i].programId == undefined)
        return i;
    }

    return 0;
  }


  const districtProgramLabel = (payrate: Payrate) => {
    if (payrate.programId === undefined || payrate.programId === 0 || payrate.programId === null)
      return payrate?.district?.name ?? "error";

    return (payrate?.district?.name ?? "error") + " - " + (payrate?.program?.name ?? "error");
  }

  const approvePayrate = () => {

  }

  /**
   * Deletes a payrate
   */
  const handleDeletePayrate = (payrateId: number) => {
    if (!payrateId || payrateId < 1) return false;
    deletePayrateAsync.run(payrateId);
  }

  const deletePayrateAsync = useAsync({
    deferFn: deleteRate,
    onResolve: (r: any) => {
      //Hide modal
      resetPayratesModal();

      //Remove payrate from table
      const id = (selectedPayrate as any)?.payrate?.payrateId;
      setPayratesActive(current => current.filter(p => p.payrateId !== id));
      setPayratesScheduled(current => current.filter(p => p.payrateId !== id));
      setPayratesPending(current => current.filter(p => p.payrateId !== id));
      setPayratesPast(current => current.filter(p => p.payrateId !== id));
    },
    onReject: (r: any) => {
      //Hide modal
      resetPayratesModal();
    }
  })

  const handleDeleteBonus = (bonusId: number) => {
    if (!bonusId || bonusId < 1) return false;
    deleteBonusAsync.run(bonusId);
  }

  const deleteBonusAsync = useAsync({
    deferFn: deleteBonusFunc,
    onResolve: (r: any) => {
      resetBonusModal();
      setBonuses(current => current.filter(x => x.bonusId !== selectedBonusId));
    },
    onReject: (r: any) => {
      resetBonusModal();
    }
  })

  return (
    <Fragment>
      <Prompt
        when={hasChanges}
        message={() => ConfirmPendingChanges}
      />
      <Layout
        isLoading={
          therapistData.isPending ||
          statesData.isPending ||
          divisionsData.isPending ||
          supervisorsData.isPending ||
          bonusData.isPending ||
          therapistsData.isPending ||
          disciplinesData.isPending ||
          districtsData.isPending ||
          save.isPending ||
          saveSupervisorAsync.isPending ||
          saveBonusAsync.isPending ||
          editBonusAsync.isPending ||
          savePayratesAsync.isPending ||
          bonusFrequencyData.isPending ||
          bonusTypesData.isPending ||
          deletePayrateAsync.isPending
        }
      >
        <Fragment>
          {state?.message && <Notification>{state?.message}</Notification>}
          {isPending && <Notification>There are pending changes to this therapist record. These have been applied to the form below.</Notification>}
          <ResponseStatus state={save} />

          {/* Modal for Supervisor/Mentor */}
          <EditModal
            open={supervisorModalOpen}
            onSave={() => saveSupervisorAsync.run()}
            onCancel={() => resetSupervisorsModal()}
            allowSave={
              (supervisorModalFirstName !== '' && supervisorModalFirstName !== undefined) &&
              supervisorModalLastName !== '' &&
              supervisorModalType !== ''
            }
            disableCancel
          >
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-2 sm:col-span-2'>
                <FieldInput
                  label='First Name'
                  disabled={readOnly}
                  minLength={1}
                  onChange={(e) => { setSupervisorModalFirstName(e.target.value); changesMade(); }}
                  value={supervisorModalFirstName}
                />
              </div>
              <div className='col-span-2 sm:col-span-2'>
                <FieldInput
                  label='Last Name'
                  disabled={readOnly}
                  minLength={1}
                  onChange={(e) => { setSupervisorModalLastName(e.target.value); changesMade(); }}
                  value={supervisorModalLastName}
                />
              </div>
            </div>
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-5 sm:col-span-5'>
                <FieldInput
                  label='Type'
                  disabled={readOnly}
                  minLength={1}
                  onChange={(e) => { setSupervisorModalType(e.target.value); changesMade(); }}
                  value={supervisorModalType}
                />
              </div>
            </div>
          </EditModal>

          {/* Modal for Bonus */}
          <EditModal
            open={bonusModalOpen}
            onSave={() => isBonusModalEditing
              ? editBonusAsync.run()
              : saveBonusAsync.run()}
            onCancel={() => { resetBonusModal(); setBonusModalOpen(false); }}
            disableCancel
            allowSave={
              bonusModalDiscipline !== undefined && bonusModalDiscipline?.toString() !== '' &&
              bonusModalAmount !== null && bonusModalAmount !== null &&
              bonusModalPaymentAmounts !== null && bonusModalPaymentAmounts !== null &&
              bonusModalType !== null && bonusModalType !== undefined && bonusModalType !== '' &&
              bonusModalFrequency !== null && bonusModalFrequency !== undefined && bonusModalFrequency !== ''
            }
            additionalButtons={<>
               {(isBillingSpecialist || isAdmin || isSuperUser) && isBonusModalEditing && <button
                className='inline-flex justify-center w-full px-4 py-2 mt-3 font-medium text-white transition duration-150 ease-in-out bg-black border border-gray-300 rounded-md shadow-sm focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm hover:bg-black'
                onClick={() => handleDeleteBonus(selectedBonusId!)}>Delete</button>
              }
              <button
                className='inline-flex justify-center w-full px-4 py-2 mt-3 font-medium text-black transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 hover:bg-bg-white focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
                onClick={resetBonusModal}
              >Cancel</button>
            </>}
          >
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-5 sm:col-span-5'>
                <label className='primary'>Division</label>
                <select
                  onChange={(e: any) => { setBonusModalDivision(e.currentTarget.value); changesMade() }}
                  value={bonusModalDivision}
                  placeholder="Select..."
                ><option></option>
                  {selectedDivisions?.sort((a, b) => {
                    return a.label.localeCompare(b.label);
                  }).map((division) =>
                    <option
                      key={`bonusFrequency-${division.value}`}
                      value={division.value}
                    >{division.label}</option>
                  )}
                </select>
              </div>
            </div>
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-5 sm:col-span-5'>
                <label className='primary'>Discipline<span className='pl-1 text-red-500'>*</span></label>
                <select
                  onChange={(e: any) => { setBonusModalDiscipline(e.currentTarget.value); changesMade(); }}
                  value={bonusModalDiscipline}
                  placeholder="Select..."
                ><option></option>
                  {therapistDisciplinesOptions.sort((a, b) => {
                    return a.label.localeCompare(b.label);
                  }).map((division) =>
                    <option
                      key={`bonusFrequency-${division.value}`}
                      value={division.value}
                    >{division.label}</option>
                  )}
                </select>
              </div>
            </div>
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-5 sm:col-span-5'>
                <label className='primary'>Type<span className='pl-1 text-red-500'>*</span></label>
                <select
                  onChange={(e: any) => { setBonusModalType(e.currentTarget.value); changesMade(); }}
                  value={bonusModalType}
                  placeholder="Select..."
                >
                  <option></option>
                  {bonusTypes.sort((a, b) => a.name.localeCompare(b.name)).map((bonusType) =>
                    <option
                      key={`bonusType-${bonusType.bonusTypeId}`}
                      value={bonusType.name}
                    >{bonusType.name}</option>
                  )}
                </select>
              </div>
            </div>
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-5 sm:col-span-5'>
                <label className='primary'>Frequency<span className='pl-1 text-red-500'>*</span></label>
                <select
                  onChange={(e: any) => { setBonusModalFrequency(e.currentTarget.value); changesMade(); }}
                  value={bonusModalFrequency}
                  placeholder="Select..."
                ><option></option>
                  {bonusFrequencies.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                  }).map((bonusFrequency) =>
                    <option
                      key={`bonusFrequency-${bonusFrequency.bonusFrequencyId}`}
                      value={bonusFrequency.name}
                    >{bonusFrequency.name}</option>
                  )}
                </select>
              </div>
            </div>
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-2 sm:col-span-5'>
                <label className='primary'>Start</label>
                <DatePicker
                  selected={bonusModalStart}
                  onChange={(date: Date) => { setBonusModalStart((date === null ? undefined : date)!); changesMade(); }}
                  dateFormat="MM/dd/yyyy"
                />
              </div>
              <div className='col-span-2 sm:col-span-5'>
                <label className='primary'>End</label>
                <DatePicker
                  minDate={bonusModalStart}
                  selected={bonusModalEnd}
                  onChange={(date: Date) => { setBonusModalEnd((date === null ? undefined : date)!); changesMade(); }}
                  dateFormat="MM/dd/yyyy"
                />
              </div>
            </div>
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-5 sm:col-span-5'>
                <FieldInput
                  label='Amount'
                  disabled={readOnly}
                  onChange={(e) => { setBonusModalAmount(e.target.value); changesMade(); }}
                  value={bonusModalAmount?.toString() ?? ''}
                  required={false}
                />
              </div>
            </div>
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-5 sm:col-span-5'>
                <FieldInput
                  label='Payment Amounts'
                  disabled={readOnly}
                  onChange={(e) => { setBonusModalPaymentAmounts(e.target.value); changesMade(); }}
                  value={bonusModalPaymentAmounts?.toString() ?? ''}
                  required={false}
                />
              </div>
            </div>
            <div className='grid grid-cols-5 gap-5 mb-6'>
              <div className='col-span-5 sm:col-span-5'>
                <label className='primary'>Comment</label>
                <textarea
                  onChange={(e) => { setBonusModalComment(e.target.value); changesMade(); }}

                  value={bonusModalComment}
                />
              </div>
            </div>
          </EditModal>

          {/* Modal for Payrates */}
          <EditModal
            open={payratesModalOpen}
            onSave={() => savePayratesAsync.run()}
            onCancel={() => resetPayratesModal()}
            allowSave={
              (payratesModalRate !== '0' && payratesModalRate !== '') &&
              payratesModalStartDate !== null &&
              payratesModalEndDate !== null &&
              Number(payratesModalDistrict) !== -1
            }
            onRemove={isDirector && (selectedPayrate ? selectedPayrate.approvedDate === null : true) ? approvePayrate : undefined}
            removeText={isDirector && (selectedPayrate ? selectedPayrate.approvedDate === null : true) ? 'Approve' : undefined}
            disableCancel
            additionalButtons={<>
              {(isOperations || isAdmin || isSuperUser) && isPayratesModalEditing && <button
                disabled={selectedPayrate && (selectedPayrate as any).payrate && (selectedPayrate as any).payrate.timesheetEntriesCount > 0 ? true : false}
                className='inline-flex justify-center w-full px-4 py-2 mt-3 font-medium text-white transition duration-150 ease-in-out bg-black border border-gray-300 rounded-md shadow-sm focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm hover:bg-black'
                onClick={() => handleDeletePayrate((selectedPayrate as any).payrate.payrateId)}>Delete</button>
              }
              <button
                className='inline-flex justify-center w-full px-4 py-2 mt-3 font-medium text-black transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 hover:bg-bg-white focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
                onClick={resetPayratesModal}
              >Cancel</button>
            </>}
          >
            <div className='pt-3 pb-6'>
              <div className='font-sm'>
                {isPayratesModalEditing ? 'Editing' : 'Adding'} {isPastRate ? 'past' : ''} rate for {payrateDiscipline?.name}<br />
                To {isPayratesModalEditing ? 'edit' : 'add'} a rate for a different discipline, please select cancel and choose a different discipline from the pay rates dropdown
              </div>
              <div className='col-span-6 pt-4 pb-6 sm:col-span-6 rate-field'>
                <div className='text-sm'><label htmlFor='rate' className='mb-3 primary'>Rate: <span className="pl-1 text-red-500">*</span></label></div>
                <input
                  type="text"
                  name='rate'
                  className='w-full'
                  onChange={(e) => { setPayratesModalRate(e.target.value.replace(/[^0-9.]/g, "")); IsAllowed([Role.BillingSpecialist], false, userRoles) ? setHasChanges(false) : changesMade(); }}
                  value={payratesModalRate}
                  disabled={isDirector}
                  readOnly={isDirector}
                />
              </div>
              <div className='col-span-6 pt-3 pb-6 sm:col-span-6'>
                <div className='text-sm'><label htmlFor='rate' className='mb-3 primary'>District / Program: <span className="pl-1 text-red-500">*</span></label></div>
                <select
                  className='block primary'
                  onChange={(e: any) => { districtOptionSelected(Number.parseInt(e.target.value)); IsAllowed([Role.BillingSpecialist], false, userRoles) ? setHasChanges(false) : changesMade(); }}
                  value={getSelectedDistrictOption()}
                  disabled={isDirector}
                >
                  {districtOptions.sort((a, b) => a.label.localeCompare(b.label)).map((option, i) => <option key={`district_option_${i}`} value={i}>{option.label}</option>)}
                </select>
              </div>
              <div className='col-span-6 pt-3 pb-6 sm:col-span-6'>
                <label className='mb-3'>Start Date:</label>
                <DatePicker
                  name='rate'
                  selected={payratesModalStartDate !== undefined && payratesModalStartDate !== null ? moment(payratesModalStartDate).toDate() : null}
                  onChange={(date: Date) => { setPayratesModalStartDate((date === null ? undefined : date)!); IsAllowed([Role.BillingSpecialist], false, userRoles) ? setHasChanges(false) : changesMade(); }}
                  dateFormat="MM/dd/yyyy"
                  disabled={isDirector}
                  readOnly={isDirector}
                />
              </div>
              <div className='col-span-6 pt-3 pb-3 mb-3 sm:col-span-6'>
                <label className='mb-3'>End Date:</label>
                <DatePicker
                  name='rate'
                  selected={payratesModalEndDate !== undefined && payratesModalEndDate !== null ? moment(payratesModalEndDate).toDate() : null}
                  minDate={payratesModalStartDate}
                  onChange={(date: Date) => { setPayratesModalEndDate((date === null ? undefined : date)!); IsAllowed([Role.BillingSpecialist], false, userRoles) ? setHasChanges(false) : changesMade(); }}
                  dateFormat="MM/dd/yyyy"
                  popperPlacement='top'
                  disabled={isDirector}
                  readOnly={isDirector}
                />
              </div>
              {(isOperations || isAdmin) && isPayratesModalEditing &&
                <div className=''>There are <span className="font-bold">{(selectedPayrate && (selectedPayrate as any).payrate ? (selectedPayrate as any).payrate.timesheetEntriesCount ?? 0 : selectedPayrate?.timesheetEntriesCount) ?? 0}</span> timesheet entries using this payrate</div>
              }
              <div className='pt-3 mb-3'></div>
            </div>
          </EditModal>

          {/* Tabs */}
          <Tab.Group>
            {/* Tab List */}
            <Tab.List className='tabs'>
              <Tab as={Fragment}>{(btn) => <button className={btn.selected ? 'active' : ''}>General</button>}</Tab>
              <Tab as={Fragment}>{(btn) => <button className={btn.selected ? 'active' : ''}>Pay Rates</button>}</Tab>
            </Tab.List>
            {/* Tab Content */}
            <Tab.Panels className='tabpanels'>
              {/* General */}
              <Tab.Panel>
                {/* General Tab Fields */}
                <div className="general-tab-form">
                  {/* Primary Profile Form */}
                  <div>
                    {/* First, MI and Last Names */}
                    <div className='grid grid-cols-5 gap-5 mb-6'>
                      <div className='col-span-2 sm:col-span-2'>
                        <FieldInput
                          name='therapist-first-name'
                          label='First Name'
                          minLength={1}
                          maxLength={70}
                          inputClass={(isPending && pendingChanges?.firstName !== null) ? "hasPendingChanges" : ""}
                          onChange={(e) => { setFirstName(e.target.value); changesMade(); }}
                          value={firstName}
                          required
                          readonly={readOnly}
                          disabled={readOnly}
                        />
                      </div>
                      <div className='col-span-1 sm:col-span-1'>
                        <FieldInput
                          name='therapist-middle-name'
                          inputClass={(isPending && pendingChanges?.middleInitial !== null) ? "hasPendingChanges" : ""}
                          label='MI'
                          maxLength={1}
                          onChange={(e) => { setMiddleInitial(e.target.value); changesMade(); }}
                          value={middleInitial}
                          readonly={readOnly}
                          disabled={readOnly}
                        />
                      </div>
                      <div className='col-span-2 sm:col-span-2'>
                        <FieldInput
                          name='therapist-last-name'
                          inputClass={(isPending && pendingChanges?.lastName !== null) ? "hasPendingChanges" : ""}
                          label='Last Name'
                          minLength={1}
                          maxLength={70}
                          onChange={(e) => { setLastName(e.target.value); changesMade(); }}
                          value={lastName}
                          required
                          readonly={readOnly}
                          disabled={readOnly}
                        />
                      </div>
                    </div>

                    {/* Email Address, Tricore ID, QuickBooks ID*/}
                    <div className='grid grid-cols-5 gap-5 mb-6'>
                      <div className='col-span-3 sm:col-span-3'>
                        <FieldInput
                          name='email-address'
                          inputClass={(isPending && pendingChanges?.emailAddress !== null) ? "hasPendingChanges" : ""}
                          label='E-Mail Address'
                          onChange={(e) => { setEmail(e.target.value); changesMade(); }}
                          value={email}
                          required
                          readonly={readOnly}
                          disabled={readOnly}
                        />
                      </div>
                      <div className='col-span-2 sm:col-span-2'>
                        <ul>
                          <li><span>Tricore ID: </span><span>{!tricoreId ? 'N/A' : tricoreId}</span></li>
                          <li><span>QuickBooks List ID: </span><span>{quickbooksListId === '' ? 'N/A' : quickbooksListId}</span></li>
                          <li><span>Primary CMS User ID: </span><span>{primaryCmsUserId === '' ? 'N/A' : primaryCmsUserId}</span></li>
                        </ul>
                      </div>
                    </div>
                    {/* Secondary Email Address*/}
                    <div className='grid grid-cols-5 gap-5 mb-6'>
                      <div className='col-span-6 sm:col-span-6'>
                        <FieldInput
                          name='email-address'
                          inputClass={(isPending && pendingChanges?.secondaryEmailAddress !== undefined && pendingChanges?.secondaryEmailAddress !== null) ? "hasPendingChanges" : ""}
                          label='Email to display on dashboard for other team members (Leave blank to use email address)'
                          onChange={(e) => { setSecondaryEmail(e.target.value); changesMade(); }}
                          value={secondaryEmail}
                          readonly={readOnly}
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                    {/* Address Lines */}
                    <div className='grid grid-cols-5 gap-5 mb-6'>
                      <div className='col-span-6 sm:col-span-6'>
                        <FieldInput
                          name='address-field'
                          inputClass={(isPending && pendingChanges?.address1 !== null) ? "hasPendingChanges" : ""}
                          label='Address 1'
                          onChange={(e) => { setAddress1(e.target.value); changesMade(); }}
                          value={address1}
                          readonly={readOnly}
                          disabled={readOnly}
                        />
                      </div>
                      <div className='col-span-6 sm:col-span-6'>
                        <FieldInput
                          name='address2-field'
                          inputClass={(isPending && pendingChanges?.address2 !== null) ? "hasPendingChanges" : ""}
                          label='Address 2'
                          onChange={(e) => { setAddress2(e.target.value); changesMade(); }}
                          value={address2}
                          readonly={readOnly}
                          disabled={readOnly}
                        />
                      </div>
                      <div className='col-span-6 sm:col-span-6'>
                        <FieldInput
                          name='city-field'
                          inputClass={(isPending && pendingChanges?.city !== null) ? "hasPendingChanges" : ""}
                          label='City'
                          onChange={(e) => { setCity(e.target.value); changesMade(); }}
                          value={city}
                          readonly={readOnly}
                          disabled={readOnly}
                        />
                      </div>
                      <div className='col-span-6 sm:col-span-6'>
                        <label className='primary'>State</label>
                        <select
                          className={((isPending && pendingChanges?.stateId !== null) ? "hasPendingChanges" : "") + " block primary"}
                          onChange={(e: any) => { setStateId(e.target.value ?? 0); changesMade(); }}
                          value={stateId}
                          disabled={readOnly}
                        >
                          {stateOptions.sort((a, b) => {
                            return a.label.localeCompare(b.label);
                          }).map((option, i) => <option key={`state_option_${i}`} value={option.value} label={option.label}>{option.label}</option>)}
                        </select>
                      </div>
                      <div className='col-span-6 sm:col-span-6'>
                        <FieldInput
                          name='zipcode-field'
                          inputClass={(isPending && pendingChanges?.zipcode !== null) ? "hasPendingChanges" : ""}
                          label='Zip Code'
                          onChange={(e) => { setZipcode(e.target.value); changesMade(); }}
                          value={zipcode}
                          readonly={readOnly}
                          disabled={readOnly}
                        />
                      </div>
                      <div className='col-span-6 sm:col-span-6'>
                        <ReactPhoneInput
                          country={'us'}
                          inputClass={(isPending && pendingChanges?.phone !== null) ? "hasPendingChanges" : ""}
                          onChange={(phone) => { setPhone(phone); changesMade(); }}
                          value={phone}
                          disableDropdown={true}
                          disableSearchIcon={true}
                          disableCountryCode={true}
                          enableAreaCodes={true}
                          placeholder={'(555) 555 5555'}
                          specialLabel={'Phone'}
                          onlyCountries={["us"]}
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Status and Payment Schedule */}
                  <div className='grid grid-cols-6 gap-5 mb-6'>
                    <div className='col-span-2 sm:col-span-3'>
                      <label className='primary'>Status</label>
                      <select
                        className='block primary'
                        onChange={(e: any) => { setStatus(e.currentTarget.value); changesMade(); }}
                        value={status}
                        disabled={readOnly}
                      >
                        {statusOptions.sort((a, b) => {
                          return a.label.localeCompare(b.label);
                        }).map((option, i) => <option key={`status_option_${i}`} value={option.label}>{option.label}</option>)}
                      </select>
                    </div>
                    <div className='col-span-2 sm:col-span-3'>
                      <label className='primary'>Payment Schedule</label>
                      <select
                        className='block primary'
                        onChange={(e: any) => { setPaymentSchedule(e.target.value); changesMade(); }}
                        value={hasValue(paymentSchedule) ? paymentScheduleOptions.filter(p => p.label === paymentSchedule)[0]?.label ?? paymentSchedule : paymentSchedule}
                        disabled={readOnly}
                      >
                        {paymentScheduleOptions.sort((a, b) => {
                          return a.label.localeCompare(b.label);
                        }).map((option, i) => <option key={`paymentschedule_option_${i}`} value={option.label}>{option.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Divisions and Disciplines */}
                  <div className='grid grid-cols-6 gap-5 mb-6'>
                    <div className='col-span-2 sm:col-span-3'>
                      <label className='primary'>Division</label>
                      <SelectMulti
                        key={`divisionMulti-${therapistDisciplinesOptions.length}`}
                        className='block primary'
                        isMulti={true}
                        options={divisionOptions}
                        defaultValue={selectedDivisions}
                        onChange={(selectedDivision: any) => { setSelectedDivisions(selectedDivision); changesMade(); }}
                        isDisabled={readOnly}
                      />
                    </div>
                    <div className='col-span-2 sm:col-span-3'>
                      <label className='primary'>Discipline <span className='pl-1 text-red-500'>*</span></label><br />{discipline?.name}
                      <SelectMulti
                        key={`disciplinesMulti-${therapistDisciplinesOptions.length}`}
                        className='block primary'
                        isMulti={true}
                        options={disciplinesOptions}
                        defaultValue={therapistDisciplinesOptions}
                        onChange={(options: any) => { handleDisciplineMulti(options); changesMade(); }}
                        isDisabled={readOnly}
                      />
                    </div>
                  </div>

                  {/* Active Districts */}
                  <div className='grid grid-cols-6 gap-2'>
                    <div className='col-span-5 sm:col-span-5'>
                      <label className='text-lg primary'>Active Districts</label>

                      <div className='mb-3 ml-3'>
                        {activeDistrictIds && activeDistrictIds.map((districtId: number, i: number) => {
                          return (
                            <div key={`adl-${i}`}>
                              {districs.find(d => d.districtId === districtId)?.name}
                            </div>
                          )
                        })}

                        {activeDistrictIds === undefined || activeDistrictIds.length === 0 && (
                          <div className='italic'>no active districts</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Supervisor/Mentor */}
                  <div className='grid grid-cols-6 gap-2'>
                    <div className='col-span-5 sm:col-span-5'><label className='text-lg primary'>Supervisor/Mentor</label></div>
                    <div className='col-span-1 sm:col-span-1'>
                      <button
                        className='primary'
                        disabled={readOnly}
                        onClick={() => { setSupervisorModalOpen(true); }}
                      >Add</button>
                    </div>
                  </div>

                  {/* Supervisor/Mentor Table */}
                  <div className='min-w-full gap-2 mb-6'>
                    {supervisors.length > 0 && (
                      <div>
                        <table className='min-w-full divide-y divide-gray-200'>
                          <thead>
                            <tr>
                              <td>Name</td>
                              <td>Type</td>
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {supervisors.map((s: Supervisor) => {
                              return (
                                <tr
                                  key={`supervisor${s.supervisorId}`}
                                  className='cursor-pointer hover:bg-gray-100'
                                  id={`supervisor${s.supervisorId}`}
                                >
                                  <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{s.lastName}, {s.firstName}</div></td>
                                  <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{s.type}</div></td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {supervisors.length === 0 && (<div className='col-span-5 mb-6 sm:col-span-5'><label className='primary text-md'>No Supervisors/Mentors Found</label></div>)}
                  </div>

                  {/* CFY Dates */}
                  <div className='grid grid-cols-4 gap-2 mb-6'>
                    <div className='col-span-2 sm:col-span-2'>
                      <label className='primary'>CFY Start Date</label>
                      <DatePicker
                        name='cfy-start-date'
                        selected={cfyStartDate}
                        onChange={(date: Date) => { setCfyStartDate((date === null ? null : date)!); changesMade(); }}
                        dateFormat="MM/dd/yyyy"
                        disabled={readOnly}
                      />
                    </div>
                    <div className='col-span-2 sm:col-span-2'>
                      <label className='primary'>CFY End Date</label>
                      <DatePicker
                        name='cfy-end-date'
                        minDate={cfyStartDate}
                        selected={cfyEndDate}
                        onChange={(date: Date) => { setCfyEndDate((date === null ? null : date)!); changesMade(); }}
                        dateFormat="MM/dd/yyyy"
                        disabled={readOnly}
                      />
                    </div>
                  </div>

                  {/* Intern Start/End Date */}
                  <div className='grid grid-cols-4 gap-2 mb-6'>
                    <div className='col-span-2 sm:col-span-2'>
                      <label className='primary'>Intern Start Date</label>
                      <DatePicker
                        name='cfy-start-date'
                        selected={internStartDate}
                        onChange={(date: Date) => { setInternStartDate((date === null ? null : date)!); changesMade(); }}
                        dateFormat="MM/dd/yyyy"
                        disabled={readOnly}
                      />
                    </div>
                    <div className='col-span-2 sm:col-span-2'>
                      <label className='primary'>Intern End Date</label>
                      <DatePicker
                        name='cfy-end-date'
                        minDate={internStartDate}
                        selected={internEndDate}
                        onChange={(date: Date) => { setInternEndDate((date === null ? null : date)!); changesMade(); }}
                        dateFormat="MM/dd/yyyy"
                        disabled={readOnly}
                      />
                    </div>
                  </div>

                  {/* Bonus */}
                  <div className='grid grid-cols-6 gap-2 mb-6'>
                    <div className='col-span-5 sm:col-span-5'><label className='text-lg primary'>Bonus</label></div>
                    <div className='col-span-1 sm:col-span-1'>
                      <button
                        className='primary'

                        onClick={() => { setBonusModalOpen(true); }}
                      >Add</button>
                    </div>
                  </div>

                  {/* Bonus Table */}
                  <div className='grid grid-cols-6 gap-2 mb-6'>
                    {bonuses.length > 0 && (
                      <div className='col-span-6'>
                        <table className='min-w-full divide-y divide-gray-200 comments-table'>
                          <thead>
                            <tr>
                              <td>Division</td>
                              <td>Discipline</td>
                              <td>Type</td>
                              <td>Frequency</td>
                              <td>Amount</td>
                              <td>Payment Amount</td>
                              <td>Start</td>
                              <td>End</td>
                              <td>Comment</td>
                              <td>Status</td>
                              {/* <td>Approved Date</td>
                              <td>Approved By</td> */}
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {bonuses.map((b: Bonus) => (
                              <tr
                                key={`bonus_${b.bonusId}`}
                                className='cursor-pointer hover:bg-gray-100'
                                onClick={() => {
                                  if (!isSuperUser && !isBillingSpecialist && !isAdmin) {
                                    return;
                                  }

                                  setSelectedBonus(b);
                                  setSelectedBonusId(b.bonusId);
                                  handleEditBonus(b);
                                }}
                                id={`bonus_${b.bonusId}`}
                              >
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.divisionName}</div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.disciplineName}</div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.type}</div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.frequency}</div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.amount}</div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.paymentAmounts}</div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'><>{b.startDate !== null ? moment(b.startDate).format('l') : 'N/A'}</></div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'><>{b.endDate !== null ? moment(b.endDate).format('l') : 'N/A'}</></div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.comments}</div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.approvedDate !== null && b.approvedBy !== null ? "Active" : (b.approvedBy !== null ? "Rejected" : "Pending")}</div></td>
                                {/* <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.approvedDate !== null ? moment(b.approvedDate).format('l') : 'N/A'}</div></td>
                                <td className='py-4 preak-word'><div className='text-sm text-gray-900'>{b.approvedByUser ? (b.approvedByUser.lastName + ", " + b.approvedByUser.firstName) : "N/A"}</div></td> */}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {bonuses.length === 0 && (
                      <div className='col-span-5 mb-6 sm:col-span-5'>
                        <label className='primary text-md'>No Bonuses Found</label>
                      </div>
                    )}
                  </div>

                  {/* Referring Therapist/Supervisee */}
                  <div className='grid grid-cols-6 gap-5 mb-6'>
                    <div className='col-span-2 sm:col-span-3'>
                      <label className='primary'>Referring Therapist</label>
                      <select
                        className='block primary'
                        onChange={(e: any) => { setReferringTherapistId(e.target.value ?? 0); changesMade(); }}
                        value={referringTherapistId}
                        disabled={readOnly}
                      >
                        <option value={0}>{`Select a therapist...`}</option>
                        {referringTherapistOptions.sort((a, b) => {
                          return a.label.localeCompare(b.label);
                        }).map((option, i) => (
                          <option key={`referringTherapist_option_${i}`} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className='col-span-2 sm:col-span-3'>
                      <label className='primary'>Supervisee</label>
                      <select
                        className='block primary'
                        onChange={(e: any) => { setSuperviseeId(e.target.value ?? 0); changesMade(); }}
                        value={superviseeId}
                        disabled={readOnly}
                      >
                        {supervisorOptions.sort((a, b) => {
                          return a.label.localeCompare(b.label);
                        }).map((option, i) => (
                          <option key={`supervisee_option_${i}`} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Comments */}
                  <div className='grid grid-cols-5 gap-5 mb-6'>
                    <div className='col-span-5 sm:col-span-5'>
                      <label className='primary'>Comments/Notes</label>
                      <textarea
                        className='block primary'
                        value={comments}
                        rows={7}
                        onChange={(e) => { setComments(e.currentTarget.value); changesMade(); }}
                        readOnly={readOnly}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                </div>
              </Tab.Panel>

              {/* Pay Rates */}
              <Tab.Panel>
                <div>
                  <div className="grid grid-cols-4 gap-2 mb-5">
                    <div className='col-span-3'>
                      <h2>Rate History</h2>
                    </div>
                    {IsAllowedExcept([Role.Director], userRoles) && (
                      <button className='col-span-1' disabled={readOnly} onClick={() => { setPayratesModalOpen(true) }}>Add Rate</button>
                    )}
                  </div>
                  <div className="mb-5">
                    <label className='primary'>Discipline</label>
                    <select
                      className='block primary'
                      onChange={(e: any) => {
                        // eslint-disable-next-line 
                        const discipline = selectedDisciplines?.filter((d) => d.disciplineId == e.currentTarget.value)[0];
                        setPayrateDiscipline(discipline);
                        setPayrateDisciplineId(e.currentTarget.value);
                        // selectedDisciplines
                        setBullhornTalentId(discipline?.therapists![0].bullhornTalentId ?? "")
                        setTalentType(discipline?.therapists![0].talentType ?? "");
                      }}
                      value={payrateDisciplineId}
                    >
                      {therapistDisciplinesActiveOptions
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map((option, i) => <option key={`payrate_discipline_option_${i}`} value={option.value}>{option.label}</option>)}
                    </select>
                  </div>
                  {/*BUllhornTalentId and TalentType*/}
                  <div className="flex items-center space-x-4 pb-4">
                    <div>
                      <label className='primary whitespace-nowrap'>BullHorn Talent ID</label>
                      {
                        (<FieldInput
                          disabled={!isAdmin && !isBillingSpecialist}
                          name='bullhorn-talent-id'
                          inputClass={(isPending && pendingChanges?.bullhornTalentId !== null) ? "hasPendingChanges" : ""}
                          onChange={(e) => {
                            selectedDisciplines?.filter((d) => {
                              if (d.disciplineId == payrateDisciplineId)
                                d.therapists![0].bullhornTalentId = e.target.value;
                            })
                            setBullhornTalentId(e.target.value);
                            changesMade();
                          }}
                          value={bullhornTalentId}
                          readonly={!isAdmin && !isBillingSpecialist}
                          noshow={!isAdmin && !isBillingSpecialist}
                        />)
                      }
                    </div>
                    <div>
                      <label className='primary whitespace-nowrap'>Talent Type</label>
                      <select
                        disabled={!isAdmin && !isBillingSpecialist}
                        className='block primary'
                        onChange={(e: any) => {
                          let Ttype = e.currentTarget.value;
                          selectedDisciplines?.filter((d) => {
                            if (d.disciplineId == payrateDisciplineId)
                              d.therapists![0].talentType = Ttype;
                          });
                          setTalentType(Ttype);
                        }}
                        value={talentType}
                      >
                        <option value="" disabled selected className='hidden'></option>
                        {Object.values(TalentType)
                          .map((value, i) => <option key={`talentType_discipline_option_${i}`} value={value}>{value}</option>)}
                      </select>
                    </div>

                  </div>


                  {/* Pending Rates */}
                  {payratesPending !== undefined &&
                    payratesPending.length > 0 && (
                      <div className='p-2 border-2 border-yellow-500'>
                        <h2 className='mt-1'>Pending Rates</h2>
                        <table className='min-w-full divide-y divide-gray-200'>
                          <thead>
                            <tr>
                              <td>District</td>
                              <td>Discipline</td>
                              <td>Pay Rate</td>
                              <td>Start Date</td>
                              <td>End Date</td>
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {payratesPending.map((pr, i) => (
                              <tr
                                key={`payratePending_${i}`}
                                className='cursor-pointer hover:bg-gray-100'
                                onClick={() => {
                                  if (isDirector && pr !== null && pr !== undefined) {
                                    setSelectedPayrate(pr);
                                    setSelectedPayrateId(pr.payrateId);
                                    handleEditPayrate(pr as PayrateRequest);
                                  }
                                }}
                              >
                                <td className='py-4 pr-2 preak-word'>
                                  <div className='text-sm text-gray-900'>
                                    {pr.districtName}
                                    {pr.programName && pr.programName !== null && (
                                      <span> - {pr.programName}</span>
                                    )}
                                  </div>
                                </td>
                                <td className='py-4 pr-2 preak-word'>
                                  <div className='text-sm text-gray-900'>
                                    {disciplines.find(d => d.disciplineId === pr.disciplineId)?.name}
                                  </div>
                                </td>
                                <td className='py-4 preak-word'>
                                  <div className='text-sm text-gray-900'>
                                    {pr?.rate?.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    })}
                                  </div>
                                </td>
                                <td className='py-4 preak-word'>
                                  <div className='text-sm text-gray-900'>
                                    {pr.startDate === null ? 'N/A' : moment(new Date(pr.startDate ?? new Date())).format('l')}
                                  </div>
                                </td>
                                <td className='py-4 preak-word'>
                                  <div className='text-sm text-gray-900'>
                                    {pr.endDate === null ? 'N/A' : moment(new Date(pr.endDate ?? new Date())).format('l')}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                  {/* Active Rates */}
                  <h2 className='mt-5'>Active Rates</h2>
                  <div>
                    {payratesActive !== undefined &&
                      payratesActive.length > 0 && (
                        <table className='min-w-full divide-y divide-gray-200'>
                          <thead>
                            <tr>
                              <td>District</td>
                              <td>Pay Rate</td>
                              <td>Start Date</td>
                              <td>End Date</td>
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {payratesActive
                              .map((pr) => (
                                <tr
                                  key={`payrateActive_${pr.payrateId}`}
                                  className='cursor-pointer hover:bg-gray-100'
                                  onClick={() => {
                                    setSelectedPayrate(pr);
                                    setSelectedPayrateId(pr.payrateId);
                                    handleEditPayrate(pr);
                                  }}
                                >
                                  <td className='py-4 pr-2 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {districtProgramLabel(pr)}
                                    </div>
                                  </td>
                                  <td className='py-4 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {pr?.rate?.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                      })}
                                    </div>
                                  </td>
                                  <td className='py-4 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {pr.startDate === null ? 'N/A' : moment(new Date(pr.startDate ?? new Date())).format('l')}
                                    </div>
                                  </td>
                                  <td className='py-4 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {pr.endDate === null ? 'N/A' : moment(new Date(pr.endDate ?? new Date())).format('l')}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      )}
                    {(payratesActive === undefined ||
                      payratesActive.length === 0) && (
                        <h2 className='text-sm'>No Active Pay Rates Found</h2>
                      )}
                  </div>

                  {/* Scheduled Rates */}
                  <h2 className='mt-5'>Scheduled Rates</h2>
                  <div>
                    {payratesScheduled !== undefined &&
                      payratesScheduled.length > 0 && (
                        <table className='min-w-full divide-y divide-gray-200'>
                          <thead>
                            <tr>
                              <td>District</td>
                              <td>Pay Rate</td>
                              <td>Start Date</td>
                              <td>End Date</td>
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {payratesScheduled
                              .map((pr) => (
                                <tr
                                  key={`payratesScheduled-${pr.payrateId}`}
                                  className='cursor-pointer hover:bg-gray-100'
                                  onClick={() => {
                                    setSelectedPayrate(pr);
                                    setSelectedPayrateId(pr.payrateId);
                                    handleEditPayrate(pr);
                                  }}
                                >
                                  <td className='py-4 pr-2 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {districtProgramLabel(pr)}
                                    </div>
                                  </td>
                                  <td className='py-4 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {pr?.rate?.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                      })}
                                    </div>
                                  </td>
                                  <td className='py-4 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {pr.startDate === null ? 'N/A' : moment(new Date(pr.startDate ?? new Date())).format('l')}
                                    </div>
                                  </td>
                                  <td className='py-4 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {pr.endDate === null ? 'N/A' : moment(new Date(pr.endDate ?? new Date())).format('l')}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      )}
                    {(payratesScheduled === undefined ||
                      payratesScheduled.length === 0) && (
                        <h2 className='text-sm'>No Scheduled Pay Rates Found</h2>
                      )}
                  </div>

                  {/* Past Rates */}
                  <h2 className="mt-5">Past Rates</h2>
                  <div>
                    {payratesPast !== undefined &&
                      payratesPast.length > 0 && (
                        <table className='min-w-full divide-y divide-gray-200'>
                          <thead>
                            <tr>
                              <td>District</td>
                              <td>Pay Rate</td>
                              <td>Start Date</td>
                              <td>End Date</td>
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {payratesPast
                              .map((pr) => (
                                <tr
                                  id={`payratePast_${pr.payrateId}`}
                                  key={`payratePast_${pr.payrateId}`}
                                  className='cursor-pointer hover:bg-gray-100'
                                  onClick={() => {
                                    if (!isSuperUser) {
                                      return;
                                    }

                                    setSelectedPayrate(pr);
                                    setSelectedPayrateId(pr.payrateId);
                                    handleEditPayrate(pr);
                                  }}
                                >
                                  <td className='py-4 pr-2 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {districtProgramLabel(pr)}
                                    </div>
                                  </td>
                                  <td className='py-4 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {pr?.rate?.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                      })}
                                    </div>
                                  </td>
                                  <td className='py-4 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {pr.startDate === null ? 'N/A' : moment(pr.startDate ?? new Date()).format('l')}
                                    </div>
                                  </td>
                                  <td className='py-4 preak-word'>
                                    <div className='text-sm text-gray-900'>
                                      {pr.endDate === null ? 'N/A' : moment(pr.endDate ?? new Date()).format('l')}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      )}
                  </div>
                  {(payratesPast === undefined ||
                    payratesPast.length === 0) && (
                      <h2 className='text-sm'>No Past Pay Rates Found</h2>
                    )}
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>

          <hr className='py-4' />

          {/* Bottom Button Row */}
          <div className='flex justify-end'>
            <SubmitButton
              text='Save Therapist'
              disabled={
                readOnly ||
                (userId === null || userId === undefined) ||
                (firstName === null || firstName === undefined || firstName === '') ||
                (lastName === null || lastName === undefined || lastName === '') ||
                (email === null || email === undefined || email === '') ||
                (therapistDisciplinesOptions === null || therapistDisciplinesOptions === undefined || therapistDisciplinesOptions.length === 0)
              }
              spinning={save.isPending}
              onClick={() => save.run()}
            />
          </div>
        </Fragment>
      </Layout >
    </Fragment >
  );
};

export default EditTherapist;